<template>
  <div class="bg-block rounded
              grid grid-cols-12">
    <div class="col-span-3 p-8 pb-0 lg:p-12 lg:pb-0 bg-transaction-gradient">
      <CreateTransactionTitle @reset="resetForm"/>
    </div>
    <div class="col-span-9 pt-8 pl-4 pr-8 lg:pt-12 lg:pl-8 lg:pr-12">
      <div class="flex items-center min-h-10">
        <CreateTransactionType :operation="transactionType"
                               :sync-banks-available="syncBanksAvailable"
                               @changeType="$emit('changeType',$event)"/>
        <TransactionOptions v-if="transactionType !== 4"
                            :operation="transactionType"
                            :options-list="transactionOptionsList"
                            :transaction-is-in-past="transactionIsInPast"
                            @toggleOption="$emit('toggleOption',$event);openForm();"/>
      </div>
    </div>
    <CreateTransactionForm v-if="transactionType !== 4"
                           ref="transactionForm"
                           :default-form-is-closed="defaultFormIsClosed"
                           :transaction-options="transactionOptionsList"
                           :transaction-type="transactionType"
                           :initial-date="initialDate"
                           @onDateChanged="date = $event"
                           @createTransaction="transaction($event)"/>
    <SynchronizeBanks v-else @bankModalClosed="$emit('updateDashboard')"/>
  </div>
</template>

<script>
import TransactionOptions from "@/components/common/Transaction/TransactionOptions/TransactionOptions";
import SynchronizeBanks from "@/components/cabinet/Money/Dashboard/AddOperation/SynchronizeBanks";
import CreateTransactionTitle from "@/components/common/CreateTransaction/CreateTransactionHead/CreateTransactionTitle";
import CreateTransactionType from "@/components/common/CreateTransaction/CreateTransactionHead/CreateTransactionType";
import CreateTransactionForm from "@/components/common/CreateTransaction/CreateTransactionForm";
import {mapActions, mapState} from "vuex";

export default {
  name: "CreateTransaction",
  props: {
    transactionType: {
      type: Number,
      required: true
    },
    transactionOptionsList: {
      type: Object
    },
    syncBanksAvailable: {
      type: Boolean
    },
    defaultFormIsClosed: {
      type: Boolean,
      default: true
    },
    initialDate: {
      type: String,
    },
  },
  components: {
    CreateTransactionForm,
    CreateTransactionType,
    CreateTransactionTitle,
    TransactionOptions,
    SynchronizeBanks,
  },
  data() {
    return {
      date: null
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('cabinet/user', ['user_info']),
    transactionIsInPast() {
      return this.$moment(this.date, 'YYYY-MM-DD').diff(this.$moment().startOf('day'), 'days') < 0;
    }
  },
  methods: {
    ...mapActions('space/transactions', ['addTransaction']),
    ...mapActions('space/transfers', ['addTransfer']),
    ...mapActions('space/plannedTransactions', ['createPlannedTransaction']),
    resetForm() {
      this.$refs.transactionForm.clearForm();
    },
    openForm() {
      if (!this.$refs.transactionForm.$data.open_form) {
        this.$refs.transactionForm.openForm();
      }
    },
    prepareData(payload) {
      let data = {}
      data.space_id = this.current_space.data.id
      data.date_time = this.current_space.data.time_enabled ?
          `${this.date} ${payload.time}:00`
          :
          `${this.date} ${this.$moment().format('HH:mm')}:00`
      data.timezone = this.$moment.tz.guess()
      data.comment = !!payload.comment ? payload.comment : null
      data.commission = (payload.account.commission_type !== null && this.transactionType !== 1) ? +payload.fee : null
      data.tag_ids = !!payload.tags.length ? payload.tags.map(i => i.id) : null

      if (this.transactionType === 3) {
        //transfer data
        data.from_id = payload.account.id
        data.to_id = payload.account_to.id
        data.sum_from = payload.account.commission_type !== null ? (+payload.sum + +payload.fee) : +payload.sum
        data.sum_to = +payload.sum_to
        data.category_id = !!payload.expenseCategory ? payload.expenseCategory.id : null;
      } else {
        //transaction data
        data.user_id = this.user_info.data.id
        data.type = this.transactionType
        data.account_id = payload.account.id
        data.sum = (payload.account.commission_type !== null && this.transactionType !== 1)
            ? (+payload.sum + +payload.fee)
            : +payload.sum
        if (this.transactionType === 2) data.expense_category_id = payload.expenseCategory.id;
        if (this.transactionType === 1) data.income_source_id = payload.incomeCategory.id;

        const repeatEnabled = this.transactionOptionsList.calendar;
        if (repeatEnabled && payload.repeat_type && !this.transactionIsInPast) {
          data.repeat_type = payload.repeat_type.id
          data.date = this.date
        }
        data.contractor_id = !!payload.contractor ? payload.contractor.id : null
      }

      return data;
    },
    async transaction(formData) {
      const transactionData = this.prepareData(formData)
      const isTransactionPlanned = !!transactionData.repeat_type;

      try {
        let res;
        if (isTransactionPlanned) {
          res = await this.createPlannedTransaction(transactionData)
        } else {
          res = this.transactionType === 3 ?
              await this.addTransfer(transactionData) :
              await this.addTransaction(transactionData)
        }

        if (res.body.success) {
          this.$emit('success')
          this.$refs.transactionForm.resetAfterSuccess();
          if (isTransactionPlanned) {
            this.$toasted.success(this.$t('transactionPlanned'));
          } else {
            this.$toasted.success(this.$t('transactionCreated'));
          }
        } else {
          this.$emit('error')
          this.$refs.transactionForm.resetAfterError();
          this.$toasted.error(this.$t('errors.response'))
        }
      } catch (err) {
        this.$emit('error')
        this.$refs.transactionForm.resetAfterError();
        this.$toasted.error(this.$t('errors.response'))
        console.log(err)
      }
    },
  },
  beforeDestroy() {
    this.$emit('setType', 2)
  },
  i18n: {
    messages: {
      en: {
        transactionCreated: 'Transaction created',
        transactionPlanned: 'Transaction planned'
      },
      ru: {
        transactionCreated: 'Операция добавлена',
        transactionPlanned: 'Операция запланирована'
      }
    }
  }
}
</script>